import React, { FocusEventHandler, useCallback, useEffect } from "react";
import { FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { IFieldErrorMessage } from '../../../types/forms.types';
import { FieldLabelKeys, FieldPlaceholderKeys } from '../../../enums/locales/forms';

import { Field } from '../Field';

import { Select as StyledSelect } from '../../styled/Select';
import { IProps as IStyledProps } from '../../styled/Select/Select';

export interface IPartialStyledProps extends IStyledProps { }

export interface IProps extends FieldProps, IPartialStyledProps {
  className?: string;
  labelKey?: FieldLabelKeys;
  placeholderKey?: FieldPlaceholderKeys,
  noBottomMargin?: boolean;
  onBlur?: (e: { target: { name: string, value: any } }) => void,
}

const Select: React.FC<IProps> = ({
  field,
  form,
  className = '',
  labelKey,
  placeholderKey,
  noBottomMargin,
  ...restProps
}) => {
  const { t } = useTranslation();
  const { name } = field;
  const { errors, touched, isSubmitting, setFieldTouched, setFieldValue, setSubmitting } = form;

  const splitedName = name.split('.')[0];

  const error = errors[splitedName];
  const isTouched = Boolean(touched[splitedName]);
  const isError = Boolean(error) && isTouched;

  useEffect(() => {
    if (isSubmitting && !isTouched) {
      setFieldTouched(splitedName, true);
    }
  }, [isSubmitting, isTouched, splitedName, setFieldTouched]);

  const onChange = useCallback((value) => {
    setFieldValue(name, value);
  }, [field, name]);

  const onBlur = useCallback(() => {
    field.onBlur({ target: { name, value: field.value } });
    setFieldTouched(name, true);
    if (restProps.onBlur) {
      restProps.onBlur({ target: { name, value: field.value } });
    }
  }, [restProps, field]);

  return (
    <Field
      className={className}
      labelKey={labelKey}
      error={error as unknown as IFieldErrorMessage}
      touched={isTouched}
      noBottomMargin={noBottomMargin}
    >
      <StyledSelect
        {...field}
        {...restProps}
        onBlur={onBlur}
        onChange={onChange}
        error={isError}
        placeholder={placeholderKey && t(placeholderKey)}
      />
    </Field>
  );
};

export default Select;
